import React, { useState, useEffect } from 'react';
import { Button, Tooltip, Space, message } from 'antd';
import { 
  ShareAltOutlined, 
  DownloadOutlined, 
  MailOutlined, 
  RollbackOutlined, 
  StarOutlined,
  CheckCircleOutlined
} from '@ant-design/icons';

/**
 * ActionCenter component for displaying action buttons
 * 
 * @param {Object} props
 * @param {Object} props.data - Optional data for the action center
 * @param {string} props.title - The title of the component
 * @param {string} props.uuid - The UUID of the component
 * @param {Object} props.activeAgent - The currently active agent with action configuration
 * @param {Array} props.componentIds - Array of component UUIDs to be sent to the backend
 * @param {string} props.sessionId - Current session ID
 * @param {Function} props.onActionComplete - Callback when action is completed
 */
const ActionCenter = ({
  data,
  title = "Action Center",
  uuid,
  activeAgent,
  componentIds = [],
  sessionId,
  onActionComplete
}) => {
  // State to store actions
  const [actions, setActions] = useState([]);
  // State to track loading state for each button
  const [loadingStates, setLoadingStates] = useState({});

  // Effect to load actions either from props data or from activeAgent config
  useEffect(() => {
    if (data && data.actions) {
      // If data is provided directly (with button text, action, etc.)
      setActions(data.actions);
    } else if (activeAgent && activeAgent.action_center) {
      // If we need to use the activeAgent's action_center configuration
      setActions(activeAgent.action_center);
    } else {
      setActions([]);
    }
  }, [data, activeAgent]);

  // Function to handle button click
  const handleActionClick = async (action) => {
    // Extract the action URL - this could be directly in the action object
    // or it could be the endpoint property from agent config
    const actionUrl = action.action || action.endpoint;
    
    if (!actionUrl) {
      message.error('Invalid action configuration');
      return;
    }

    try {
      // Set loading state for this specific button
      setLoadingStates(prev => ({ ...prev, [actionUrl]: true }));

      // Prepare payload with component IDs and any other necessary data
      const payload = {
        componentIds: componentIds,
        action: action.name || action.button_name || action["button text"],
        agentId: activeAgent?.id,
        uuid: uuid // Include the ActionCenter's own UUID if needed
      };

      // Call the endpoint
      const response = await fetch(actionUrl, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${sessionId}`
        },
        body: JSON.stringify(payload),
        credentials: 'include'
      });

      if (!response.ok) {
        throw new Error(`Action failed with status ${response.status}`);
      }

      // Process successful response
      const data = await response.json();
      
      // Show success message
      const actionName = action.name || action.button_name || action["button text"] || "Action";
      message.success(data.message || `${actionName} completed successfully`);
      
      // Call the callback if provided
      if (onActionComplete) {
        onActionComplete(action, data);
      }
    } catch (error) {
      console.error('Error executing action:', error);
      const actionName = action.name || action.button_name || action["button text"] || "Action";
      message.error(`Failed to execute ${actionName}: ${error.message}`);
    } finally {
      // Clear loading state
      setLoadingStates(prev => ({ ...prev, [actionUrl]: false }));
    }
  };

  // Helper function to get the appropriate icon
  const getIconComponent = (iconName) => {
    if (!iconName) return <CheckCircleOutlined />;
    
    const iconLower = iconName.toString().replace('.png', '').toLowerCase();
    
    switch (iconLower) {
      case 'share':
        return <ShareAltOutlined />;
      case 'download':
        return <DownloadOutlined />;
      case 'mail':
        return <MailOutlined />;
      case 'back':
        return <RollbackOutlined />;
      case 'ratings':
      case 'feedback':
        return <StarOutlined />;
      case 'complete':
        return <CheckCircleOutlined />;
      default:
        return <CheckCircleOutlined />; // Default icon
    }
  };

  // Return the action center component with buttons in a horizontal layout
  return (
    <div style={{ 
      textAlign: 'center',
      padding: '4px',
      background: '#f0f0f0',
      borderRadius: '8px',
      boxShadow: '0 2px 5px rgba(0, 0, 0, 0.1)',
      marginBottom: '16px'
    }}>
      <Space size="middle" style={{ display: 'flex', justifyContent: 'center' }}>
        {actions.map((action, index) => {
          // Determine the action URL and button text from different data structures
          const actionUrl = action.action || action.endpoint || '';
          const buttonText = action["button text"] || action.button_name || action.name || 'Action';
          const icon = action.icon || null;
          
          return (
            <Tooltip key={`action-${index}`} title={buttonText}>
              <Button
                type="primary"
                icon={getIconComponent(icon)}
                onClick={() => handleActionClick(action)}
                loading={loadingStates[actionUrl]}
                style={{
                  backgroundColor: '#0066FF', // Blue color
                  borderColor: '#0275d8',
                  borderRadius: '4px',
                  fontWeight: 'bold',
                  minWidth: '40px',
                  height: '40px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center'
                }}
              >
                {buttonText}
              </Button>
            </Tooltip>
          );
        })}
      </Space>
    </div>
  );
};

export default ActionCenter;